import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import type { TenantProfileRun } from "../../api/client.ts";
import { isJobActive } from "../../utils/status.ts";
import TenantResearchProgress from "./TenantResearchProgress.tsx";

/**
 * The workflows onboarding drafts from the profile: progress while the designer
 * works, then what it created (paused). Nothing for a run that does not design.
 */
export default function DesignedWorkflows({ run }: { run: TenantProfileRun | undefined }) {
  if (!run) return null;
  const output = run.output ?? {};
  const designing = run.stage === "designing" && isJobActive(run.status);
  const workflows = output.workflows ?? [];

  if (!designing && !workflows.length && !output.workflow_design_error) return null;

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        Workflows
      </Typography>
      {designing && (
        <Stack spacing={1}>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Drafting signal and prospecting workflows from this profile. You can finish now; they
            are created paused either way.
          </Typography>
          <TenantResearchProgress run={run} />
        </Stack>
      )}
      {output.workflow_design_error && (
        <Alert severity="info">
          {output.workflow_design_error} You can build workflows yourself under Workflows.
        </Alert>
      )}
      {!designing && workflows.length > 0 && (
        <Stack spacing={1}>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Created paused. Review them under Workflows and switch on the ones you want.
          </Typography>
          {workflows.map((wf) => (
            <Box
              key={wf.id ?? wf.name}
              sx={{ border: 1, borderColor: "divider", borderRadius: 1, px: 1.5, py: 1 }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, flex: 1 }}>
                  {wf.name}
                </Typography>
                <Chip
                  size="small"
                  variant="outlined"
                  label={wf.trigger_type === "prospect_order" ? "Prospecting" : "Signal"}
                />
              </Box>
              {wf.rationale && (
                <Typography variant="caption" component="div" sx={{ color: "text.secondary" }}>
                  {wf.rationale}
                </Typography>
              )}
            </Box>
          ))}
        </Stack>
      )}
    </Box>
  );
}
